import React, { useCallback, useEffect, useMemo, useState } from 'react'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import EmailIcon from '@mui/icons-material/Email'
import TextSnippetOutlined from '@mui/icons-material/TextSnippetOutlined'
import { SaveRounded } from '@mui/icons-material'
import { Box, Button, CircularProgress, Grid, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { DateTimePicker } from '@mui/x-date-pickers'
import SmartTextField from '../generic/smarttextfield/SmartTextField'
import SmartSelect from '../generic/smartSelect/SmartSelect'
import SmartMateriaUITable from '../generic/SmartMateriaUITable'
import { useDateFormatter } from '../generic/hooks/useDateFormatter'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import { apiGet, apiPost, apiPut } from '../generic/Api_Functions'
import AlertUI from '../generic/AlertUI'
import useUser from '../generic/hooks/useUser/useUser'
import { SmartDialog } from '../generic/utilities/SmartDialog'
import MailForm from '../generic/mail/MailForm'
import Document from '../configuration/document/Document'
import { getRandomUUID } from '../generic/utilities/randomuuid'
import { systemValueLabel } from '../generic/systemValues/systemValueLabel'

const ACTION_TYPE_OPTIONS = [
  { value: 'CORRECTIVE', labelKey: 'Corrective' },
  { value: 'CONTAINMENT', labelKey: 'Containment' }
]

const DEFAULT_MAIL_TEMPLATE = 'incident-report'

const FETCH_ALL_PAGINATION = {
  page: 0,
  size: -1,
  columns: [],
  genericSearch: '',
  rows: [],
  allSelected: false,
  primaryKeys: ['id'],
  sortKey: [],
  sortMode: []
}

const createDefaultForm = () => ({
  actionType: 'CORRECTIVE',
  actionStatus: 'PENDING',
  actionResult: 'PENDING',
  code: '',
  description: '',
  comment: '',
  expirationDate: null,
  responsible: {},
  selectedScopes: [],
  selectedScopeAffecteds: [],
  tempUUID: getRandomUUID(),
  uuid: getRandomUUID()
})

const createEmptyRelatedOptions = () => ({
  affecteds: [],
  scopes: [],
  scopeAffecteds: []
})

const normalizeApiList = (response) => {
  if (Array.isArray(response)) return response
  if (Array.isArray(response?.content)) return response.content
  return []
}

const normalizeMailRecipientList = (response) => {
  return normalizeApiList(response)
    .map((item) => {
      if (typeof item === 'string') return item.trim()
      return String(item?.email ?? item?.mail ?? item?.address ?? item?.to ?? item?.code ?? item?.name ?? '').trim()
    })
    .filter(Boolean)
}

const normalizeRecipients = (value) => {
  const items = Array.isArray(value) ? value : []
  return items
    .map((item) => String(item || '').trim())
    .filter(Boolean)
}

const normalizeUniqueStringList = (value) => {
  const seen = new Set()

  return normalizeRecipients(value).filter((item) => {
    const normalizedKey = item.toLowerCase()
    if (seen.has(normalizedKey)) return false
    seen.add(normalizedKey)
    return true
  })
}

const normalizeUniqueOptionList = (value) => {
  const items = Array.isArray(value) ? value : []
  const seen = new Set()

  return items.filter((item) => {
    const key = String(item?.id ?? item?.name ?? '').trim()
    if (!key || seen.has(key)) return false
    seen.add(key)
    return true
  })
}

const normalizeDocumentAttachmentOptions = (response) => {
  return normalizeApiList(response)
    .map((document) => ({
      id: document?.id,
      code: document?.fileName,
      name: document?.documentName || document?.fileName || ''
    }))
    .filter((document) => document.id != null && document.code && document.name)
}

const getUserLabel = (user) => {
  return String(user?.username || user?.name || user?.fullName || user?.code || '').trim()
}

const getEntityDisplayName = (value) => {
  if (!value) return ''
  if (typeof value === 'string') return value.trim()
  return String(value?.name || value?.fullName || value?.code || value?.fabricationOrder || value?.title || value?.id || '').trim()
}

const getResourceFromItem = (item) => {
  return item?.resource || item?.rawMaterial || item?.material || item?.qualityIncidenceAffectedRawMaterial || item?.qualityIncidenceAffectedRawMaterialDto || item || null
}

const getResourceLabel = (item) => {
  const resource = getResourceFromItem(item)
  const code = String(item?.resourceCode ?? resource?.resourceCode ?? resource?.code ?? '').trim()
  const name = String(item?.resourceName ?? resource?.resourceName ?? resource?.name ?? '').trim()
  const label = [code, name].filter(Boolean).join(' - ')
  if (label) return label
  const resourceId = item?.resourceId ?? resource?.resourceId ?? resource?.id
  return resourceId == null ? '' : String(resourceId)
}

const getQualityIncidenceUuid = (item) => {
  if (!item) return ''

  const source = item?.qualityIncidence || item?.qualityIncidenceDto || item?.incidence || item
  return String(
    source?.uuid ??
    source?.id ??
    source?.qualityIncidenceUuid ??
    source?.qualityIncidenceId ??
    ''
  ).trim()
}

const getScopeUuid = (scope) => {
  if (!scope) return ''

  const source = scope?.qualityIncidenceScope || scope
  return String(
    source?.uuid ??
    source?.scopeUuid ??
    source?.id ??
    source?.qualityIncidenceScopeUuid ??
    source?.qualityIncidenceScopeId ??
    ''
  ).trim()
}

const getActionTargetUuid = (item) => {
  if (!item) return ''

  return String(
    item?.uuid ??
    item?.id ??
    item?.qualityIncidenceAffectedUuid ??
    item?.qualityIncidenceScopeUuid ??
    item?.qualityIncidenceScopeAffectedUuid ??
    item?.resourceId ??
    item?.scopeAffectedUuid ??
    item?.affectedUuid ??
    ''
  ).trim()
}

const getScopeLabel = (scope) => {
  const fabricationOrderCode = getEntityDisplayName(scope?.fabricationOrder) || String(scope?.fabricationOrderCode ?? '').trim()
  const jobName = getEntityDisplayName(scope?.node) || String(scope?.jobName ?? '').trim()
  return [fabricationOrderCode, jobName].filter(Boolean).join(' - ')
}

// One option per (OF, job) scope so the operator can target a specific job. Scope-affecteds are
// later grouped per (OF, resource), but the save flow only fans them out to the scopes that the
// user actually picked here — that's what preserves the per-job granularity.
const normalizeScopeOptions = (scopes) => {
  const rows = Array.isArray(scopes) ? scopes : []

  return normalizeUniqueOptionList(rows.map((item, index) => {
    const scopeUuid = getScopeUuid(item) || `scope-${index}`
    const name = getScopeLabel(item)
    if (!name) return null
    return { id: scopeUuid, name }
  }).filter(Boolean))
}

// One option per scope-affected entity, labelled "FO - job - resource" so the operator can tell
// which job each candidate belongs to and pick the right one when the same material exists in
// several jobs.
const normalizeScopeAffectedOptions = (scopes) => {
  const rows = Array.isArray(scopes) ? scopes : []
  const seen = new Set()
  const options = []

  rows.forEach((scope, scopeIndex) => {
    const scopeUuid = getScopeUuid(scope) || `scope-${scopeIndex}`
    const fabricationOrderCode = getEntityDisplayName(scope?.fabricationOrder) || String(scope?.fabricationOrderCode ?? '').trim()
    const jobName = getEntityDisplayName(scope?.node) || String(scope?.jobName ?? '').trim()
    const affecteds = Array.isArray(scope?.affected)
      ? scope.affected
      : Array.isArray(scope?.resources)
        ? scope.resources
        : []

    affecteds.forEach((item, affectedIndex) => {
      const resourceLabel = getResourceLabel(item)
      if (!resourceLabel) return
      const uuid = String(item?.uuid ?? item?.id ?? `${scopeUuid}-${affectedIndex}`)
      if (seen.has(uuid)) return
      seen.add(uuid)
      const name = [fabricationOrderCode, jobName, resourceLabel].filter(Boolean).join(' - ')
      options.push({
        id: uuid,
        name,
        scopeId: String(scopeUuid),
        entries: [{ scopeAffectedUuid: uuid, scopeUuid: String(scopeUuid) }]
      })
    })
  })

  return options
}

const getActionMailSubject = ({ form, incidence }) => {
  const incidenceCode = String(incidence?.code || '').trim()
  const incidenceTitle = String(incidence?.title || incidence?.name || '').trim()

  return [incidenceCode, incidenceTitle, String(form.code || '').trim()].filter(Boolean).join(' - ')
}

const formatMailDate = (value) => {
  if (!value) return ''
  if (value instanceof Date) return value.toLocaleString()
  if (typeof value?.toDate === 'function') return value.toDate().toLocaleString()
  return String(value)
}

const normalizeDatePickerValue = (value) => {
  if (!value) return null
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value
  if (typeof value?.toDate === 'function') {
    const date = value.toDate()
    return Number.isNaN(date.getTime()) ? null : date
  }
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

const formatSelectedOptionNames = (items) => {
  const names = normalizeUniqueOptionList(items)
    .map((item) => String(item?.name || '').trim())
    .filter(Boolean)

  return names.join(', ')
}

const getActionUuid = (action) => action?.uuid || action?.qualityIncidenceActionUuid || action?.id

const filterDeletedActions = (data, deletedIds) => {
  const ids = new Set(deletedIds || [])
  if (ids.size === 0) return data

  const isDeleted = (row) => ids.has(getActionUuid(row))

  if (Array.isArray(data)) return data.filter((row) => !isDeleted(row))

  if (Array.isArray(data?.content)) {
    const filteredContent = data.content.filter((row) => !isDeleted(row))
    return {
      ...data,
      content: filteredContent
    }
  }

  return data
}

const getColumnFilterValue = (pagination, field) => {
  const filterIndex = Array.isArray(pagination?.columnFilters)
    ? pagination.columnFilters.indexOf(field)
    : -1

  if (filterIndex < 0 || !Array.isArray(pagination?.columnFilterValues)) return ''

  return String(pagination.columnFilterValues[filterIndex] || '').trim().toLowerCase()
}

const removeColumnFilter = (pagination, field) => {
  if (!Array.isArray(pagination?.columnFilters)) return pagination

  const filterIndex = pagination.columnFilters.indexOf(field)
  if (filterIndex < 0) return pagination

  return {
    ...pagination,
    columnFilters: pagination.columnFilters.filter((_, index) => index !== filterIndex),
    columnFilterValues: Array.isArray(pagination.columnFilterValues)
      ? pagination.columnFilterValues.filter((_, index) => index !== filterIndex)
      : pagination.columnFilterValues
  }
}

const removeSortField = (pagination, field) => {
  if (!Array.isArray(pagination?.sortKey)) return pagination

  const sortIndex = pagination.sortKey.indexOf(field)
  if (sortIndex < 0) return pagination

  return {
    ...pagination,
    sortKey: pagination.sortKey.filter((_, index) => index !== sortIndex),
    sortMode: Array.isArray(pagination.sortMode)
      ? pagination.sortMode.filter((_, index) => index !== sortIndex)
      : pagination.sortMode
  }
}

const removeLocalResourceRequestFields = (pagination) => ({
  ...removeSortField(removeColumnFilter(pagination, 'resource'), 'resource'),
  columns: Array.isArray(pagination?.columns)
    ? pagination.columns.filter((column) => column !== 'resource')
    : pagination?.columns
})

const filterActionResourcePage = (data, resourceFilter, pagination) => {
  if (!resourceFilter) return data

  const rows = Array.isArray(data)
    ? data
    : Array.isArray(data?.content)
      ? data.content
      : []

  const filteredRows = rows.filter((row) => String(row?.resource || '').toLowerCase().includes(resourceFilter))
  const page = Math.max(0, Number(pagination?.page || 0))
  const size = Number(pagination?.size || filteredRows.length || 0)
  const pagedRows = size > 0 ? filteredRows.slice(page * size, page * size + size) : filteredRows

  if (Array.isArray(data?.content)) {
    return {
      ...data,
      content: pagedRows,
      totalElements: filteredRows.length,
      number: page,
      size
    }
  }

  return pagedRows
}

const actionCanHaveChildren = (action) => Boolean(
  getActionUuid(action) &&
  (action?.qualityIncidence || action?.scopeDto || action?.qualityIncidenceScope) &&
  action?.actionParent === null
)

const getActionIncidenceSource = (action, fallbackRow) => {
  return action?.qualityIncidence ||
    action?.qualityIncidenceDto ||
    action?.scopeDto?.qualityIncidence ||
    action?.scopeDto?.qualityIncidenceDto ||
    action?.qualityIncidenceScope?.qualityIncidence ||
    action?.qualityIncidenceScope?.qualityIncidenceDto ||
    action?.affected?.qualityIncidence ||
    action?.affected?.qualityIncidenceDto ||
    action?.scopeAffected?.qualityIncidence ||
    action?.scopeAffected?.qualityIncidenceDto ||
    action?.scopeAffected?.scope?.qualityIncidence ||
    action?.scopeAffected?.scope?.qualityIncidenceDto ||
    fallbackRow?.qualityIncidence ||
    fallbackRow?.qualityIncidenceDto ||
    null
}

const getActionTargetLabel = ({ row, normalizedType, sourceSummary }) => {
  if (normalizedType === 'qualityincidence') return sourceSummary
  if (normalizedType === 'qualityincidencescope') return getScopeLabel(row) || sourceSummary
  if (normalizedType === 'qualityincidenceaffected') return getResourceLabel(row) || sourceSummary

  if (normalizedType === 'qualityincidencescopeaffected') {
    const scopeLabel = getScopeLabel(row?.qualityIncidenceScope || row?.scope || row)
    const resourceLabel = getResourceLabel(row)
    return [scopeLabel, resourceLabel].filter(Boolean).join(' - ') || sourceSummary
  }

  return sourceSummary
}

const getActionMailData = ({ form, incidence, normalizedType, row, sourceSummary, responsibleName, deletedChildActions = [] }) => {
  const incidenceCode = String(incidence?.code || '').trim()
  const incidenceTitle = String(incidence?.title || incidence?.name || '').trim()
  const targetLabel = getActionTargetLabel({ row, normalizedType, sourceSummary })
  const scopes = formatSelectedOptionNames(form.selectedScopes)
  const scopeAffectedResources = formatSelectedOptionNames(form.selectedScopeAffecteds)
  const deletedChildActionResources = deletedChildActions
    .map((childAction) => String(childAction?.resource || getResourceLabel(childAction) || '').trim())
    .filter(Boolean)
    .join(', ')

  return [
    { name: 'Incidence', value: [incidenceCode, incidenceTitle].filter(Boolean).join(' - ') },
    targetLabel && { name: 'Action target', value: targetLabel },
    { name: 'Action code', value: String(form.code || '').trim() },
    { name: 'Action type', value: String(form.actionType || '').trim() },
    { name: 'Expiration date', value: formatMailDate(form.expirationDate) },
    responsibleName && { name: 'Responsible', value: responsibleName },
    scopes && { name: 'Scopes', value: scopes },
    scopeAffectedResources && { name: 'Scope affected resources', value: scopeAffectedResources },
    deletedChildActionResources && { name: 'Removed affected resources', value: deletedChildActionResources },
    { name: 'Description', value: String(form.description || '').trim() },
    { name: 'Comment', value: String(form.comment || '').trim() }
  ].filter(Boolean)
}

const ActionForm = ({
  row,
  type,
  onClose,
  onSaved,
  action = null,
  unlinkParentOnSave = false
}) => {
  const theme = useTheme()
  const { t_ } = useSmartTranslation()
  const { format } = useDateFormatter()
  const { user } = useUser()
  const [form, setForm] = useState(() => createDefaultForm())
  const [mailRecipients, setMailRecipients] = useState([])
  const [actionStatusValues, setActionStatusValues] = useState([])
  const [relatedOptions, setRelatedOptions] = useState(() => createEmptyRelatedOptions())
  const [isLoadingRelatedOptions, setIsLoadingRelatedOptions] = useState(false)
  const [userGroups, setUserGroups] = useState([])
  const [isSaving, setIsSaving] = useState(false)
  const [isSaved, setIsSaved] = useState(false)
  const [hasTriedSave, setHasTriedSave] = useState(false)
  const [deletedChildActionUuids, setDeletedChildActionUuids] = useState([])
  const [deletedChildActions, setDeletedChildActions] = useState([])
  const [showSendEmail, setShowSendEmail] = useState(false)
  const [isDownloadingFile, setIsDownloadingFile] = useState(false)
  const [showDocument, setShowDocument] = useState(false)
  const [notificationSent, setNotificationSent] = useState(false)
  const [possibleDocuments, setPossibleDocuments] = useState([])
  const [mailTemplate, setMailTemplate] = useState({
    to: [],
    subject: '',
    body: '',
    templateName: DEFAULT_MAIL_TEMPLATE,
    templateVariables: {},
    attachments: []
  })
  const [AlertElement, showAlert] = AlertUI()

  const normalizedType = String(type || 'qualityIncidence').trim().toLowerCase()
  const isQualityIncidence = normalizedType === 'qualityincidence'
  const isQualityIncidenceScope = normalizedType === 'qualityincidencescope'
  const isQualityIncidenceAffected = normalizedType === 'qualityincidenceaffected'
  const isQualityIncidenceScopeAffected = normalizedType === 'qualityincidencescopeaffected'
  const isEditMode = Boolean(action)

  const showScopesSelector = isQualityIncidence && !isEditMode
  const showScopeAffectedSelector = (isQualityIncidence || isQualityIncidenceScope) && !isEditMode

  const scope = isQualityIncidenceScope ? row : null
  const incidenceSource = isEditMode
    ? (getActionIncidenceSource(action, row) || (isQualityIncidenceScope ? (row?.qualityIncidence || row?.qualityIncidenceDto || row?.incidence || row) : row))
    : (isQualityIncidenceScope ? (row?.qualityIncidence || row?.qualityIncidenceDto || row?.incidence || row) : row)
  const incidenceUuid = getQualityIncidenceUuid(incidenceSource)
  const scopeUuid = getScopeUuid(scope)
  const rowUuid = getActionTargetUuid(row)

  useEffect(() => {
    setForm({
      ...createDefaultForm(),
      uuid: action?.uuid ?? getRandomUUID(),
      actionType: String(action?.actionType || action?.type || 'CORRECTIVE').trim().toUpperCase(),
      actionStatus: String(action?.actionStatus || 'PENDING').trim().toUpperCase(),
      actionResult: String(action?.actionResult || 'PENDING').trim().toUpperCase(),
      code: String(action?.code || '').trim(),
      description: String(action?.description || '').trim(),
      comment: String(action?.comment || '').trim(),
      expirationDate: normalizeDatePickerValue(action?.expirationDate),
      responsible: action?.responsible || {}
    })
    setIsSaved(false)
    setHasTriedSave(false)
    setDeletedChildActionUuids([])
    setDeletedChildActions([])
    setRelatedOptions(createEmptyRelatedOptions())
    setPossibleDocuments([])
    setNotificationSent(Boolean(action?.notificationSent))
  }, [action, action?.notificationSent, incidenceUuid, rowUuid, scopeUuid, normalizedType])

  useEffect(() => {
    let cancelled = false

    const loadMailRecipients = async () => {
      try {
        const response = await apiGet('mailReceptor')
        if (cancelled) return
        setMailRecipients(normalizeUniqueStringList(normalizeMailRecipientList(response)))
      } catch (_) {
        if (cancelled) return
        setMailRecipients([])
      }
    }

    const loadActionStatusValues = async () => {
      try {
        const response = await apiGet('getQualityIncidenceActionStatus')
        if (cancelled) return
        setActionStatusValues(Array.isArray(response) ? response : [])
      } catch (_) {
        if (cancelled) return
        setActionStatusValues([])
      }
    }

    loadMailRecipients()
    loadActionStatusValues()

    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    let cancelled = false

    const loadUserGroups = async () => {
      try {
        const response = await apiGet('userGroups')
        if (cancelled) return
        setUserGroups(normalizeUniqueOptionList(normalizeApiList(response)))
      } catch (_) {
        if (cancelled) return
        setUserGroups([])
      }
    }

    loadUserGroups()

    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    let cancelled = false

    const loadRelatedOptions = async () => {
      const shouldLoadQualityIncidence = isQualityIncidence && incidenceUuid
      const shouldLoadScopeAffecteds = isQualityIncidenceScope && scopeUuid

      if (!shouldLoadQualityIncidence && !shouldLoadScopeAffecteds) {
        setRelatedOptions(createEmptyRelatedOptions())
        return
      }

      setIsLoadingRelatedOptions(true)

      try {
        let scopes = null
        let scopeAffecteds = []

        if (shouldLoadQualityIncidence) {
          scopes = Array.isArray(incidenceSource?.scopes) ? incidenceSource.scopes : null

          if (!scopes) {
            const scopeResponse = await apiPost(`qualityIncidenceScope/${incidenceUuid}/true`, FETCH_ALL_PAGINATION)
            scopes = normalizeApiList(scopeResponse)
          }

          scopeAffecteds = normalizeScopeAffectedOptions(scopes)
        }
        if (shouldLoadScopeAffecteds) {
          let rawScopeAffecteds = []

          if (Array.isArray(scope?.affected) || Array.isArray(scope?.resources)) {
            rawScopeAffecteds = Array.isArray(scope?.affected) ? scope.affected : scope.resources
          } else {
            const scopeAffectedResponse = await apiPost(`qualityIncidenceScopeAffected/${scopeUuid}/true`, FETCH_ALL_PAGINATION)
            rawScopeAffecteds = normalizeApiList(scopeAffectedResponse)
          }

          scopeAffecteds = normalizeScopeAffectedOptions([{ ...scope, affected: rawScopeAffecteds }])
        }

        if (cancelled) return

        setRelatedOptions({
          affecteds: [],
          scopes: shouldLoadQualityIncidence ? normalizeScopeOptions(scopes) : [],
          scopeAffecteds
        })
      } catch (_) {
        if (cancelled) return
        setRelatedOptions(createEmptyRelatedOptions())
      } finally {
        if (!cancelled) setIsLoadingRelatedOptions(false)
      }
    }

    loadRelatedOptions()

    return () => {
      cancelled = true
    }
  }, [incidenceSource?.scopes, incidenceUuid, isQualityIncidence, isQualityIncidenceScope, scope, scopeUuid])

  const actionTypeOptions = useMemo(() => {
    return ACTION_TYPE_OPTIONS.map((option) => ({ value: option.value, label: t_(option.labelKey) }))
  }, [t_])

  const actionTypeLabelByValue = useMemo(() => {
    const map = new Map()
    actionTypeOptions.forEach((option) => map.set(option.value, option.label))
    return map
  }, [actionTypeOptions])

  const selectedResponsible = useMemo(
    () => userGroups.find((group) => group.id === form.responsible?.id) || null,
    [userGroups, form.responsible]
  )

  const actionStatusOptions = useMemo(() => {
    return (actionStatusValues || []).map((value) => ({ value: String(value), label: t_(String(value)) }))
  }, [actionStatusValues, t_])

  const actionStatusLabelByValue = useMemo(() => {
    const map = new Map()
    actionStatusOptions.forEach((option) => map.set(option.value, option.label))
    return map
  }, [actionStatusOptions])

  const launchedByLabel = getUserLabel(user)
  const sourceSummary = isQualityIncidenceScope
    ? [
        String(incidenceSource?.code || '').trim(),
        getEntityDisplayName(scope?.fabricationOrder) || String(scope?.fabricationOrderCode || '').trim(),
        getEntityDisplayName(scope?.node) || String(scope?.jobName || '').trim()
      ].filter(Boolean).join(' - ')
    : [String(incidenceSource?.code || '').trim(), String(incidenceSource?.title || incidenceSource?.name || '').trim()].filter(Boolean).join(' - ')

  const mailData = useMemo(() => {
    return getActionMailData({
      form,
      incidence: incidenceSource,
      normalizedType,
      row,
      sourceSummary,
      responsibleName: form.responsible?.name,
      deletedChildActions
    })
  }, [deletedChildActions, form, incidenceSource, normalizedType, row, sourceSummary])

  const mailTemplateWithAttachments = useMemo(() => ({
    ...mailTemplate,
    attachments: possibleDocuments
  }), [mailTemplate, possibleDocuments])

  const isActionTypeValid = String(form.actionType || '').trim().length > 0
  const isCodeValid = String(form.code || '').trim().length > 0
  const isDescriptionValid = String(form.description || '').trim().length > 0
  const isExpirationDateValid = Boolean(form.expirationDate)
  // When the action is launched from the incidence root, the operator must pick at least one
  // target — either a whole scope or a scope-affected resource. Both anchor the action; requiring
  // a scope explicitly when the user already picked specific resources is redundant.
  const isTargetRequired = isQualityIncidence && !isEditMode
  const hasAnyTargetSelected = form.selectedScopes.length > 0 || form.selectedScopeAffecteds.length > 0
  const isTargetSelectionValid = !isTargetRequired || hasAnyTargetSelected
  const isFormValid = isActionTypeValid && isCodeValid && isDescriptionValid && isExpirationDateValid && isTargetSelectionValid
  // Email notification is reserved for corrective actions.
  const isCorrectiveAction = String(form.actionType || '').trim().toUpperCase() === 'CORRECTIVE'

  const updateField = useCallback((field, value) => {
    setForm((prev) => {
      if (field === 'selectedScopes' || field === 'selectedScopeAffecteds') {
        return {
          ...prev,
          [field]: normalizeUniqueOptionList(value)
        }
      }

      return { ...prev, [field]: value }
    })
  }, [])

  const saveAction = useCallback(async () => {
    const hasActionTarget = (
      (isQualityIncidence && incidenceUuid) ||
      (isQualityIncidenceScope && scopeUuid) ||
      (isQualityIncidenceAffected && rowUuid) ||
      (isQualityIncidenceScopeAffected && rowUuid)
    )

    if (!isFormValid || !hasActionTarget || isSaving) return

    // Scope and scope-affected are independent targets. A picked scope means "action against the
    // whole scope"; a picked scope-affected means "action against that specific resource". A
    // scope-affected attached to a scope the user also picked nests inside it; one whose scope
    // wasn't picked travels as an orphan inside the synthetic { id: null, affected: [...] } entry
    // — the backend already accepts that shape.
    const selectedScopeUuids = form.selectedScopes
      .map((scope) => String(scope?.id ?? ''))
      .filter(Boolean)
    const selectedScopeUuidSet = new Set(selectedScopeUuids)
    const allScopeAffectedEntries = form.selectedScopeAffecteds
      .flatMap((item) => (Array.isArray(item?.entries) ? item.entries : []))

    const selectedScopesWithAffecteds = selectedScopeUuids.map((scopeUuid) => ({
      id: scopeUuid,
      affected: allScopeAffectedEntries
        .filter((entry) => String(entry.scopeUuid) === scopeUuid)
        .map((entry) => ({ uuid: entry.scopeAffectedUuid }))
    }))
    const orphanScopeAffecteds = allScopeAffectedEntries
      .filter((entry) => !selectedScopeUuidSet.has(String(entry.scopeUuid)))
      .map((entry) => ({ uuid: entry.scopeAffectedUuid }))
    const qualityIncidenceScopes = orphanScopeAffecteds.length
      ? [
          ...selectedScopesWithAffecteds,
          { id: null, affected: orphanScopeAffecteds }
        ]
      : selectedScopesWithAffecteds
    const scopeDtoAffected = allScopeAffectedEntries.map((entry) => ({ uuid: entry.scopeAffectedUuid }))

    const createPayload = ({ qualityIncidence = null, affected = null, scopeDto = null, scopeAffected = null }) => ({
      uuid: form.uuid,
      qualityIncidence,
      affected,
      scopeDto,
      scopeAffected,
      qualityIncidenceScope: scopeDto,
      type: String(form.actionType || 'CORRECTIVE').trim().toUpperCase(),
      actionType: String(form.actionType || 'CORRECTIVE').trim().toUpperCase(),
      code: String(form.code || '').trim(),
      description: String(form.description || '').trim(),
      comment: String(form.comment || '').trim() || null,
      launchedBy: user?.id ? { id: user.id } : null,
      expirationDate: form.expirationDate || null,
      responsible: form.responsible?.id ? form.responsible : null,
      actionStatus: form.actionStatus || null,
      actionResult: form.actionResult || null,
      executionDate: action?.executionDate || null,
      ...(isEditMode ? { actionUUIDSToDelete: deletedChildActionUuids } : {})
    })

    let payloads = []

    if (isQualityIncidence) {
      if (isEditMode) {
        payloads = [createPayload({ qualityIncidence: { uuid: incidenceUuid } })]
      } else {
        // Count atomic targets: an unaffected scope = 1, every nested scope-affected = 1, every
        // orphan scope-affected = 1. With a single target we skip the qualityIncidence wrapper so
        // the backend doesn't create an extra parent action that just mirrors the only child.
        const targetCount = selectedScopesWithAffecteds.reduce(
          (acc, scope) => acc + Math.max(1, scope.affected.length),
          0
        ) + orphanScopeAffecteds.length

        if (targetCount === 1) {
          if (orphanScopeAffecteds.length === 1) {
            payloads = [createPayload({ scopeAffected: orphanScopeAffecteds[0] })]
          } else {
            const onlyScope = selectedScopesWithAffecteds[0]
            if (onlyScope.affected.length === 1) {
              payloads = [createPayload({ scopeAffected: onlyScope.affected[0] })]
            } else {
              payloads = [createPayload({ scopeDto: { id: onlyScope.id, affected: [] } })]
            }
          }
        } else {
          payloads = [createPayload({
            qualityIncidence: {
              uuid: incidenceUuid,
              affected: [],
              scopes: qualityIncidenceScopes
            }
          })]
        }
      }
    }

    if (isQualityIncidenceScope) {
      payloads = [createPayload({
        scopeDto: isEditMode
          ? { id: scopeUuid }
          : {
              id: scopeUuid,
              affected: scopeDtoAffected
            }
      })]
    }

    if (isQualityIncidenceAffected) {
      payloads = [createPayload({ affected: { uuid: rowUuid } })]
    }

    if (isQualityIncidenceScopeAffected) {
      payloads = [createPayload({ scopeAffected: { uuid: rowUuid } })]
    }

    try {
      setIsSaving(true)
      const savedActions = isEditMode
        ? [await apiPut('qualityIncidenceAction', payloads[0])]
        : await Promise.all(payloads.map((payload) => apiPost('qualityIncidenceAction/create/' + form.tempUUID, payload)))
      showAlert({
        severity: 'success',
        title: t_(isEditMode ? 'Quality incidence action updated' : 'Quality incidence action saved')
      })
      onSaved?.(savedActions)
      setIsSaved(true)
      setMailTemplate({
        to: normalizeUniqueStringList(mailRecipients),
        subject: getActionMailSubject({ form, incidence: incidenceSource }),
        body: '',
        templateName: DEFAULT_MAIL_TEMPLATE,
        templateVariables: {},
        attachments: []
      })
      return savedActions
    } catch (error) {
      showAlert({
        severity: 'error',
        title: t_('Error saving quality incidence action'),
        message: error?.message || t_('Please check the form and try again')
      })
      return null
    } finally {
      setIsSaving(false)
    }
  }, [deletedChildActionUuids, form, incidenceSource, incidenceUuid, isEditMode, isFormValid, isQualityIncidence, isQualityIncidenceAffected, isQualityIncidenceScope, isQualityIncidenceScopeAffected, isSaving, mailRecipients, onSaved, rowUuid, scopeUuid, showAlert, t_, user?.id, action?.executionDate])
  const handleSaveRequest = useCallback((event) => {
    event.preventDefault()
    setHasTriedSave(true)
    if (!isFormValid || isSaving || isSaved) return
    saveAction()
  }, [isFormValid, isSaved, isSaving, saveAction])

  const fetchActionDocuments = useCallback(async () => {
    // On a freshly created action the `action` prop is still empty, so fall back to the form uuid
    // (the persisted action uuid, since create uses `uuid: form.uuid`). Without this, documents
    // only attach to the mail after leaving and re-opening the action in edit mode.
    const actionUuid = getActionUuid(action) || form.uuid
    if (!actionUuid) {
      setPossibleDocuments([])
      return []
    }

    try {
      const documents = await apiGet('document/getAll/QUALITY_INCIDENCE_ACTION/' + actionUuid)
      const normalizedDocuments = normalizeDocumentAttachmentOptions(documents)
      setPossibleDocuments(normalizedDocuments)
      return normalizedDocuments
    } catch (_) {
      setPossibleDocuments([])
      return []
    }
  }, [action, form.uuid])

  const handleSendEmail = useCallback(async () => {
    if (!isSaved || isSaving || notificationSent) return
    await fetchActionDocuments()
    setShowSendEmail(true)
  }, [fetchActionDocuments, isSaved, isSaving, notificationSent])

  const handleEmailSent = useCallback(async () => {
    if (!form.uuid || notificationSent) return

    try {
      await apiPut('setNotificationSentTrue/' + form.uuid)
      setNotificationSent(true)
      setShowSendEmail(false)
      onSaved?.()
    } catch (error) {
      showAlert({
        severity: 'error',
        title: t_('Error updating notification status'),
        message: error?.message || t_('Please check the form and try again')
      })
    }
  }, [form.uuid, notificationSent, onSaved, showAlert, t_])

  const relatedHelperText = useCallback((options, emptyMessage) => {
    if (isLoadingRelatedOptions) return t_('Loading')
    if (options.length === 0) return emptyMessage
    return ''
  }, [isLoadingRelatedOptions, t_])

  const childActionColumns = useMemo(() => [
    {
      name: t_('status'),
      field: 'actionStatus',
      render: (row) => systemValueLabel(t_, 'QualityIncidenceActionStatusEnum', row.actionStatus)
    },
    {
      name: t_('Result'),
      field: 'actionResult',
      render: (row) => systemValueLabel(t_, 'QualityIncidenceActionResultEnum', row.actionResult)
    },
    {
      name: t_('resource'),
      field: 'resource'
    },
    {
      name: t_('Expiration date'),
      field: 'expirationDate',
      type: 'datetime'
    }
  ], [t_])

  const childActionActions = useMemo(() => ({
    custom: [{
      contextMenu: false,
      render: (childAction) => {
        const childActionUuid = getActionUuid(childAction)
        const isMade = String(childAction?.actionStatus || '').trim().toUpperCase() === 'MADE'
        const disabled = !childActionUuid || isSaved || isMade
        return (
          <Tooltip title={isMade ? t_('Can not delete because action is made') : t_('Delete')}>
            <span>
              <IconButton
                size='small'
                disabled={disabled}
                onClick={(event) => {
                  event.stopPropagation()
                  if (disabled) return
                  setDeletedChildActionUuids((prev) => Array.from(new Set([...prev, childActionUuid])))
                  setDeletedChildActions((prev) => {
                    if (prev.some((deletedAction) => getActionUuid(deletedAction) === childActionUuid)) return prev
                    return [...prev, childAction]
                  })
                }}
              >
                <DeleteOutlineRoundedIcon color={disabled ? 'disabled' : 'error'} />
              </IconButton>
            </span>
          </Tooltip>
        )
      }
    }]
  }), [isSaved, t_])

  const childActionDataFetch = useCallback(async (pagination) => {
    const actionUuid = getActionUuid(action)
    if (!actionUuid || !actionCanHaveChildren(action)) return []
    const resourceFilter = getColumnFilterValue(pagination, 'resource')
    const requestPagination = resourceFilter ? { ...removeLocalResourceRequestFields(pagination), page: 0, size: -1 } : pagination
    const response = await apiGet('action/' + actionUuid, requestPagination)
    const filteredResponse = filterDeletedActions(response, deletedChildActionUuids)
    return filterActionResourcePage(filteredResponse, resourceFilter, pagination)
  }, [action, deletedChildActionUuids])

  return (
    <>
      <SmartDialog
        closeCallback={() => setShowDocument(false)}
        accept={false}
        cancel={false}
        close
        disableButtons={isDownloadingFile}
        renderComponent={
          <Document
            reference={{ id: form?.uuid, code: form?.code }}
            resourceType='QUALITY_INCIDENCE_ACTION'
            isDownloadingFile={isDownloadingFile}
            setIsDownloadingFile={setIsDownloadingFile}
            defaultName={form?.code || t_('Name still undefined')}
            // Only stage documents against the temp uuid while the action does not yet exist. Once
            // it is persisted (edit mode or after the first save) documents must be created directly
            // against the action uuid, otherwise they stay in the temp store and are never linked.
            referenceTempUUID={isEditMode || isSaved ? '' : form.tempUUID}
            referenceUUID={form.uuid}
          />
        }
        setOpen={showDocument}
      />
      <Box component='form' noValidate onSubmit={handleSaveRequest} sx={{ height: '100%', overflow: 'auto' }}>
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {AlertElement}
          <SmartDialog
            title={{ icon: <EmailIcon />, render: <Typography variant='h5'>{t_('Send Email')}</Typography> }}
            setOpen={showSendEmail}
            closeCallback={() => setShowSendEmail(false)}
            accept={false}
            cancel={false}
            close
            paperWidth='40rem'
            renderComponent={(
              <MailForm
                template={mailTemplateWithAttachments}
                data={mailData}
                attachmentOptions={possibleDocuments}
                canEditContent={false}
                onSave={handleEmailSent}
              />
          )}
          />

          <Stack direction='row' justifyContent='space-between' alignItems='center'>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, minWidth: 0, whiteSpace: 'nowrap' }}>
              <Typography variant='h6' sx={{ fontWeight: 600, flexShrink: 0 }}>
                {t_('Quality incidence action')}
              </Typography>
              <Typography color='text.secondary' sx={{ overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 500 }}>
                {sourceSummary}
              </Typography>
            </Box>

            <Stack direction='row' spacing={1}>
              <Button
                type='button'
                sx={{ borderColor: 'transparent' }}
                color='primary'
                variant='outlined'
                onClick={() => setShowDocument(true)}
              >
                <TextSnippetOutlined sx={{ fontSize: '1.2em' }} />
                <Typography sx={{ marginLeft: '0.4em', fontSize: '0.875rem' }}>
                  {t_('Documents').toUpperCase()}
                </Typography>
              </Button>
              <Button
                type='button'
                sx={{ borderColor: 'transparent' }}
                color='primary'
                variant='outlined'
                onClick={handleSendEmail}
                disabled={!isSaved || isSaving || notificationSent || !isCorrectiveAction}
              >
                {isSaving
                  ? <CircularProgress size={18} color='inherit' />
                  : <EmailIcon sx={{ fontSize: '1.2em' }} />}
                <Typography sx={{ marginLeft: '0.4em', fontSize: '0.875rem' }}>
                  {t_(notificationSent ? t_('Email sent') : t_('Send email')).toUpperCase()}
                </Typography>
              </Button>
              <Button
                type='submit'
                disabled={isSaved || isSaving}
                sx={{ borderColor: 'transparent' }}
                color='primary'
                variant='outlined'
              >
                {isSaving
                  ? <CircularProgress size={18} color='inherit' />
                  : <SaveRounded sx={{ fontSize: '1.2em' }} />}
                <Typography sx={{ marginLeft: '0.4em', fontSize: '0.875rem' }}>
                  {(isSaving ? t_('Saving') : t_('Save')).toUpperCase()}
                </Typography>
              </Button>
            </Stack>
          </Stack>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <SmartSelect
                label={t_('Action type') + ' *'}
                value={actionTypeLabelByValue.get(form.actionType) || ''}
                selectableOptions={actionTypeOptions.map((option) => option.label)}
                onChange={(label) => {
                  const option = actionTypeOptions.find((item) => item.label === label)
                  updateField('actionType', option?.value || 'CORRECTIVE')
                }}
                error={hasTriedSave && !isActionTypeValid}
                disabled={isSaved}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <SmartTextField
                label={t_('Code')}
                value={form.code}
                onChange={(value) => updateField('code', value)}
                checkError={hasTriedSave ? (value) => (String(value || '').trim() ? '' : `${t_('Code')} ${t_('Can not be empty').toLocaleLowerCase()}`) : undefined}
                forceCheckError={hasTriedSave}
                required
                disabled={isSaved}
                style={{ width: '100%' }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <SmartTextField
                label={t_('Launched by')}
                value={launchedByLabel}
                onChange={() => {}}
                disabled
                style={{ width: '100%' }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DateTimePicker
                label={t_('Expiration date')}
                slotProps={{
                  textField: {
                    InputLabelProps: { shrink: true },
                    fullWidth: true,
                    required: true,
                    error: hasTriedSave && !isExpirationDateValid
                  }
                }}
                format={format.dateTime}
                ampm={false}
                value={form.expirationDate}
                onChange={(value) => updateField('expirationDate', value)}
                disabled={isSaved}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <SmartSelect
                label={t_('Responsible')}
                value={selectedResponsible}
                selectableOptions={userGroups}
                onChange={(value) => updateField('responsible', value || {})}
                disabled={isSaved}
                disableClearable={false}
                autoSelectFirst={false}
                width='100%'
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <SmartSelect
                label={t_('status')}
                value={actionStatusLabelByValue.get(form.actionStatus) || ''}
                selectableOptions={actionStatusOptions.map((option) => option.label)}
                onChange={(label) => {
                  const option = actionStatusOptions.find((item) => item.label === label)
                  updateField('actionStatus', option?.value || 'PENDING')
                }}
                // Only a real parent action (one that actually has child actions) has its status derived
                // from its children; standalone and child actions can edit their own status.
                disabled={isSaved || Boolean(action?.hasChildren)}
              />
            </Grid>

            {(showScopesSelector || showScopeAffectedSelector) && (
              <Grid item xs={12}>
                {/* Grouped block instead of two independent fields with their own asterisk —
                    operator should see "pick at least one of these", not "both required". */}
                <Box
                  sx={{
                    border: 1,
                    borderColor: hasTriedSave && !isTargetSelectionValid
                      ? theme.palette.error.main
                      : 'rgba(255,255,255,0.12)',
                    borderRadius: 1.5,
                    p: 2,
                    transition: 'border-color 120ms ease-in-out'
                  }}
                >
                  <Stack direction='row' alignItems='baseline' spacing={1} sx={{ mb: 1.5 }}>
                    <Typography
                      variant='subtitle2'
                      sx={{
                        fontWeight: 700,
                        color: hasTriedSave && !isTargetSelectionValid
                          ? theme.palette.error.main
                          : theme.palette.text.primary
                      }}
                    >
                      {t_('Action target')}{isTargetRequired ? ' *' : ''}
                    </Typography>
                    <Typography variant='caption' sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                      {t_('pick at least one')}
                    </Typography>
                  </Stack>
                  <Grid container spacing={2}>
                    {showScopesSelector && (
                      <Grid item xs={12} sm={6}>
                        <SmartSelect
                          multiple
                          label={t_('Scopes')}
                          value={form.selectedScopes}
                          selectableOptions={relatedOptions.scopes}
                          onChange={(value) => updateField('selectedScopes', value)}
                          disabled={isSaved || isLoadingRelatedOptions || relatedOptions.scopes.length === 0}
                          helperText={relatedHelperText(relatedOptions.scopes, t_('No scopes available'))}
                          disableClearable={false}
                          autoSelectFirst={false}
                        />
                      </Grid>
                    )}

                    {showScopeAffectedSelector && (
                      <Grid item xs={12} sm={isQualityIncidenceScope || !showScopesSelector ? 12 : 6}>
                        <SmartSelect
                          multiple
                          label={t_('Scope affected resources')}
                          value={form.selectedScopeAffecteds}
                          selectableOptions={relatedOptions.scopeAffecteds}
                          onChange={(value) => updateField('selectedScopeAffecteds', value)}
                          disabled={isSaved || isLoadingRelatedOptions || relatedOptions.scopeAffecteds.length === 0}
                          helperText={relatedHelperText(relatedOptions.scopeAffecteds, t_('No scope affected resources available'))}
                          disableClearable={false}
                          autoSelectFirst={false}
                        />
                      </Grid>
                    )}
                  </Grid>
                </Box>
              </Grid>
            )}

            <Grid item xs={12}>
              <Box
                component='fieldset'
                sx={{
                  borderColor: !hasTriedSave || isDescriptionValid ? '#ffffff4d' : theme.palette.error.main,
                  borderRadius: 1,
                  padding: '4px 14px 14px',
                  minWidth: 0,
                  '&:focus-within': {
                    borderColor: !hasTriedSave || isDescriptionValid ? theme.palette.primary.main : theme.palette.error.main
                  }
                }}
              >
                <Box
                  component='legend'
                  sx={{
                    px: 1,
                    fontSize: '0.75rem',
                    textAlign: 'left',
                    color: !hasTriedSave || isDescriptionValid ? undefined : theme.palette.error.main
                  }}
                >
                  {t_('Description')} *
                </Box>
                <Box
                  component='textarea'
                  sx={{
                    width: '100%',
                    border: 'none',
                    outline: 'none',
                    resize: 'vertical',
                    font: 'inherit',
                    background: 'transparent',
                    padding: 0,
                    minHeight: '7rem'
                  }}
                  onChange={(event) => updateField('description', event.target.value)}
                  value={form.description}
                  disabled={isSaved}
                />
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box
                component='fieldset'
                sx={{
                  borderColor: '#ffffff4d',
                  borderRadius: 1,
                  padding: '4px 14px 14px',
                  minWidth: 0,
                  '&:focus-within': {
                    borderColor: theme.palette.primary.main
                  }
                }}
              >
                <Box component='legend' sx={{ px: 1, fontSize: '0.75rem', textAlign: 'left' }}>
                  {t_('Comment')}
                </Box>
                <Box
                  component='textarea'
                  sx={{
                    width: '100%',
                    border: 'none',
                    outline: 'none',
                    resize: 'vertical',
                    font: 'inherit',
                    background: 'transparent',
                    padding: 0,
                    minHeight: '5rem'
                  }}
                  onChange={(event) => updateField('comment', event.target.value)}
                  value={form.comment}
                  disabled={isSaved}
                />
              </Box>
            </Grid>

            {isEditMode && actionCanHaveChildren(action) && (
              <Grid item xs={12}>
                <Box>
                  <Box component='legend' sx={{ px: 1, ml: 0, mb: 1, textAlign: 'left' }}>
                    {t_('Affected resources')}
                  </Box>
                  <SmartMateriaUITable
                    tableTuneUniqueKey={`qualityIncidenceActionChildrenEdit${getActionUuid(action) || ''}`}
                    columns={childActionColumns}
                    dataFetch={childActionDataFetch}
                    actions={childActionActions}
                    forceLoad={deletedChildActionUuids.join('|')}
                    pageable
                    dense
                    toolbarDisplay='none'
                    tablePageSize={5}
                    sticky={false}
                    hideExport
                  />
                </Box>
              </Grid>
            )}

          </Grid>
        </Box>
      </Box>
    </>
  )
}
export default ActionForm
